import React from 'react';

import noop from 'lodash/noop';

import {useEvent, useOnChange, useOnMount, useOnUnmount} from '@edna/hooks';

//TODO: need tests

const IN_BROWSER = typeof navigator !== 'undefined';

// these browsers don't fully support navigator.onLine, so we need to use a polling backup
const unsupportedUserAgentsPattern = /Windows.*Chrome|Windows.*Firefox|Linux.*Chrome/;

export type TProps = {
  onChange?: (online: boolean) => void;
  pollingUrl?: string;
  pollingUrlMethod?: string;
  render?: (online: boolean) => React.ReactElement;
  innerRef?: React.RefObject<any>;
};

const DEFAULT_POLLING_URL = '/favicon.ico';
const DEFAULT_POLLING_METHOD = 'HEAD';

const POLLING_TIMEOUT = 5_000;
const POLLING_ENABLED = IN_BROWSER && unsupportedUserAgentsPattern.test(navigator.userAgent);

const ping = (url: string, method: string) =>
  new Promise((resolve) => {
    const isOnline = () => resolve(true);
    const isOffline = () => resolve(false);

    const xhr = new XMLHttpRequest();

    xhr.onerror = isOffline;
    xhr.ontimeout = isOffline;

    xhr.onreadystatechange = () => {
      if (xhr.readyState === xhr.HEADERS_RECEIVED) {
        if (xhr.status) {
          isOnline();
        } else {
          isOffline();
        }
      }
    };

    xhr.open(method, url);
    xhr.timeout = POLLING_TIMEOUT;
    xhr.send();
  });

const OfflineDetector = React.memo<TProps>(
  ({
    onChange = noop,
    pollingUrl = DEFAULT_POLLING_URL,
    pollingUrlMethod = DEFAULT_POLLING_METHOD,
    render,
    innerRef,
  }) => {
    const pollingId = React.useRef<number | undefined>(undefined);

    const [online, setOnline] = React.useState(IN_BROWSER ? navigator.onLine : true);

    const goOnline = () => setOnline(true);

    const goOffline = () => setOnline(false);

    const check = () => {
      ping(pollingUrl, pollingUrlMethod).then((isOnline) => (isOnline ? goOnline() : goOffline()));
    };

    const startPolling = () => {
      pollingId.current = window.setInterval(check, POLLING_TIMEOUT);
    };

    const stopPolling = () => {
      if (pollingId.current) {
        clearInterval(pollingId.current);
      }
    };

    useOnMount(() => {
      if (POLLING_ENABLED) {
        startPolling();
      }
    });

    useOnUnmount(() => {
      if (pollingId.current) {
        stopPolling();
      }
    });

    useEvent('online', goOnline, {target: window});
    useEvent('offline', goOffline, {target: window});

    useOnChange(() => {
      onChange(online);
    }, [online]);

    React.useImperativeHandle(innerRef, () => ({
      check,
    }));

    return render ? render(online) : null;
  },
);

export default OfflineDetector;
